import { useEffect, useRef } from "react";

/**
 * Lightweight canvas particles — gold motes + faint neon sparks.
 * Performance-friendly: throttled, paused when offscreen.
 */
export const Particles = ({ density = 0.00012 }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });
  const visibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    let mounted = true;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      sizeRef.current = { w, h, dpr };
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.max(40, Math.floor(w * h * density));
      const arr = [];
      for (let i = 0; i < count; i++) {
        arr.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.4 + 0.3,
          vx: (Math.random() - 0.5) * 0.12,
          vy: -Math.random() * 0.18 - 0.04,
          a: Math.random() * 0.7 + 0.2,
          tw: Math.random() * Math.PI * 2,
          neon: Math.random() < 0.08,
        });
      }
      particlesRef.current = arr;
    };

    const draw = () => {
      if (!mounted) return;
      const { w, h } = sizeRef.current;
      ctx.clearRect(0, 0, w, h);
      const ps = particlesRef.current;
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        p.x += p.vx;
        p.y += p.vy;
        p.tw += 0.02;
        if (p.y < -10) {
          p.y = h + 10;
          p.x = Math.random() * w;
        }
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        const tw = (Math.sin(p.tw) + 1) * 0.5;
        const alpha = p.a * (0.5 + tw * 0.5);
        ctx.beginPath();
        if (p.neon) {
          ctx.fillStyle = `rgba(0, 212, 255, ${alpha * 0.55})`;
        } else {
          ctx.fillStyle = `rgba(212, 175, 55, ${alpha * 0.7})`;
        }
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    const onVisibility = () => {
      visibleRef.current = !document.hidden;
      if (visibleRef.current && !rafRef.current) {
        rafRef.current = requestAnimationFrame(draw);
      } else if (!visibleRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      mounted = false;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default Particles;
